import type { EffectDefinition } from './types';
import { FilmGrainEffect } from './effects/cinematic/filmGrain';
import { VignetteEffect } from './effects/cinematic/vignette';
import { ChromaticAberrationEffect } from './effects/cinematic/chromaticAberration';
import { AnamorphicStreakEffect } from './effects/cinematic/anamorphicStreak';
import { SparklesEffect } from './effects/cinematic/sparkles';
import { DreamHazeEffect } from './effects/cinematic/dreamHaze';

/** Premium cinematic / finishing effects. */
export const CINEMATIC_EFFECT_DEFINITIONS: EffectDefinition[] = [
  {
    type: 'film_grain',
    label: 'Film Grain (35mm)',
    category: 'cinematic',
    defaultParameters: { amount: 0.45, size: 0.35 },
    parameterSchema: [
      { key: 'amount', label: 'Amount', min: 0, max: 1.5, step: 0.05 },
      { key: 'size', label: 'Grain Size', min: 0, max: 1, step: 0.05 },
    ],
    create: (id, clip) => new FilmGrainEffect(id, clip),
  },
  {
    type: 'vignette',
    label: 'Cinematic Vignette',
    category: 'cinematic',
    defaultParameters: { amount: 0.55, softness: 0.55, roundness: 0.7 },
    parameterSchema: [
      { key: 'amount', label: 'Amount', min: 0, max: 1.5, step: 0.05 },
      { key: 'softness', label: 'Softness', min: 0, max: 1, step: 0.05 },
      { key: 'roundness', label: 'Roundness', min: 0, max: 1, step: 0.05 },
    ],
    create: (id, clip) => new VignetteEffect(id, clip),
  },
  {
    type: 'chromatic_aberration',
    label: 'Chromatic Aberration',
    category: 'lens',
    defaultParameters: { amount: 0.55, radial: 0.85 },
    parameterSchema: [
      { key: 'amount', label: 'Amount', min: 0, max: 2, step: 0.05 },
      { key: 'radial', label: 'Edge Bias', min: 0, max: 1, step: 0.05 },
    ],
    create: (id, clip) => new ChromaticAberrationEffect(id, clip),
  },
  {
    type: 'anamorphic_streak',
    label: 'Anamorphic Streak',
    category: 'light',
    defaultParameters: { intensity: 0.7, width: 0.45, positionY: 0.35, hue: 0.12 },
    parameterSchema: [
      { key: 'intensity', label: 'Intensity', min: 0, max: 2, step: 0.05 },
      { key: 'width', label: 'Width', min: 0.05, max: 1.5, step: 0.05 },
      { key: 'positionY', label: 'Position Y', min: 0, max: 1, step: 0.01 },
      { key: 'hue', label: 'Hue', min: 0, max: 1, step: 0.01 },
    ],
    create: (id, clip) => new AnamorphicStreakEffect(id, clip),
  },
  {
    type: 'sparkles',
    label: 'Magic Sparkles',
    category: 'particles',
    defaultParameters: { density: 0.55, size: 0.7, twinkle: 0.8, brightness: 1 },
    parameterSchema: [
      { key: 'density', label: 'Density', min: 0, max: 1, step: 0.05 },
      { key: 'size', label: 'Size', min: 0.2, max: 2, step: 0.05 },
      { key: 'twinkle', label: 'Twinkle', min: 0, max: 2, step: 0.05 },
      { key: 'brightness', label: 'Brightness', min: 0, max: 2, step: 0.05 },
    ],
    create: (id, clip) => new SparklesEffect(id, clip),
  },
  {
    type: 'dream_haze',
    label: 'Dream Haze',
    category: 'cinematic',
    defaultParameters: { amount: 0.45, bloom: 0.7, lift: 0.25 },
    parameterSchema: [
      { key: 'amount', label: 'Haze', min: 0, max: 1, step: 0.05 },
      { key: 'bloom', label: 'Soft Bloom', min: 0, max: 2, step: 0.05 },
      { key: 'lift', label: 'Highlight Lift', min: 0, max: 1, step: 0.05 },
    ],
    create: (id, clip) => new DreamHazeEffect(id, clip),
  },
];

export const CINEMATIC_EFFECT_ORDER = [
  'dream_haze',
  'anamorphic_streak',
  'chromatic_aberration',
  'sparkles',
  'film_grain',
  'vignette',
] as const;
